import { leafBodyHeight } from './waternoseGeometry.js';

export const MAX_WINDOWS = 10;
const edges = ['start', 'end', 'center', 'ratio', 'stretch'];

// Start means left for X and top for Y. Offsets are measured to the opening
// edge (centre-to-centre for centre anchoring); ratio uses the available travel.
export function anchorPosition(anchor, extent, size) {
  const { edge, offsetMm = 0, ratio = 0.5 } = anchor || {};
  if (!edges.includes(edge) || !Number.isFinite(offsetMm) || !Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    throw new Error('Anchor requires an edge, finite offset and a position from 0–100%.');
  }
  if (edge === 'start' || edge === 'stretch') return offsetMm;
  if (edge === 'end') return extent - size - offsetMm;
  return (extent - size) * (edge === 'center' ? 0.5 : ratio) + offsetMm;
}

function openingSize(anchor, extent, fixedSize, label) {
  if (anchor?.edge !== 'stretch') return fixedSize;
  if (!Number.isFinite(fixedSize) || fixedSize <= 0 || fixedSize > 4000) {
    throw new Error(`${label}: keep a valid fixed dimension up to 4000 mm before enabling resizing.`);
  }
  const { offsetMm, endOffsetMm } = anchor;
  if (![offsetMm, endOffsetMm].every(n => Number.isFinite(n) && n >= 0)) {
    throw new Error(`${label}: both edge distances must be non-negative numbers in millimetres.`);
  }
  const size = extent - offsetMm - endOffsetMm;
  if (size <= 0) throw new Error(`${label}: the edge distances leave no room for the opening. Reduce the distances or increase the door size.`);
  return size;
}

export function anchoredOpening(o) {
  return { widthMm: o.widthMm, heightMm: o.heightMm,
    anchorX: { edge: 'start', offsetMm: o.xMm },
    anchorY: { edge: 'end', offsetMm: o.yMm } };
}

export const defaultWindowLayout = () => ({
  mode: 'vertical', spacing: 'scaled', gapMm: 160, referenceMm: 2057,
  mainAnchor: { edge: 'start', offsetMm: 220 },
  crossAnchor: { edge: 'center', offsetMm: 0 },
  startMargin: { value: 220, unit: 'mm' }, endMargin: { value: 220, unit: 'mm' }
});

function marginMm(margin, extent) {
  if (!margin || !['mm', '%'].includes(margin.unit) || !Number.isFinite(margin.value) || margin.value < 0) {
    throw new Error('Edge margin must be non-negative, in millimetres or percent.');
  }
  return margin.unit === '%' ? extent * margin.value / 100 : margin.value;
}

export function resolveWindowLayout({ widthMm, heightMm, openings = [], windowLayout, waternose, thicknessMm }) {
  if (!Array.isArray(openings) || openings.length > MAX_WINDOWS) throw new Error('Up to 10 window openings are allowed.');
  if (![widthMm, heightMm].every((n) => Number.isFinite(n) && n > 0)) throw new Error('Door dimensions must be positive.');
  heightMm = leafBodyHeight({ heightMm, thicknessMm, waternose });
  const individual = !windowLayout || windowLayout.mode === 'individual';
  if (!individual && (openings.some(o => o?.anchorX?.edge === 'stretch' || o?.anchorY?.edge === 'stretch') ||
      windowLayout.mainAnchor?.edge === 'stretch' || windowLayout.crossAnchor?.edge === 'stretch')) {
    throw new Error('Resizing between edge distances requires Individual openings.');
  }
  openings = openings.map((o, i) => o && ({ ...o,
    widthMm: openingSize(o.anchorX, widthMm, o.widthMm, `Opening ${i + 1} width`),
    heightMm: openingSize(o.anchorY, heightMm, o.heightMm, `Opening ${i + 1} height`) }));
  openings.forEach((o, i) => {
    if (!o || ![o.widthMm, o.heightMm].every((n) => Number.isFinite(n) && n > 0)) {
      throw new Error(`Opening ${i + 1}: width and height must be positive numbers in millimetres.`);
    }
  });
  if (individual) {
    return { heightMm, gapMm: null, openings: openings.map((o) => ({
      widthMm: o.widthMm, heightMm: o.heightMm,
      xMm: o.anchorX ? anchorPosition(o.anchorX, widthMm, o.widthMm) : o.xMm,
      yMm: o.anchorY ? heightMm - o.heightMm - anchorPosition(o.anchorY, heightMm, o.heightMm) : o.yMm
    })) };
  }
  const layout = windowLayout;
  if (!['vertical', 'horizontal'].includes(layout.mode) || !['fixed', 'scaled', 'equal'].includes(layout.spacing)) {
    throw new Error('Unknown window layout or spacing rule.');
  }
  const vertical = layout.mode === 'vertical';
  const extent = vertical ? heightMm : widthMm;
  const sizes = openings.map((o) => vertical ? o.heightMm : o.widthMm);
  const total = sizes.reduce((a, b) => a + b, 0);
  let gap = 0, start = 0;
  if (layout.spacing === 'equal') {
    start = marginMm(layout.startMargin, extent);
    const available = extent - start - marginMm(layout.endMargin, extent);
    if (available < total) throw new Error(`Openings do not fit between the margins: ${Math.round(total)} mm required, ${Math.round(available)} mm available. Reduce their dimensions, count or edge margins.`);
    if (openings.length > 1) gap = (available - total) / (openings.length - 1);
    else start += (available - total) / 2;
  } else {
    if (!Number.isFinite(layout.gapMm) || layout.gapMm < 0 ||
        (layout.spacing === 'scaled' && (!Number.isFinite(layout.referenceMm) || layout.referenceMm <= 0))) {
      throw new Error('Gap and reference dimension must be valid.');
    }
    gap = layout.gapMm * (layout.spacing === 'scaled' ? extent / layout.referenceMm : 1);
    start = anchorPosition(layout.mainAnchor, extent, total + Math.max(0, openings.length - 1) * gap);
  }
  const resolved = openings.map((o, i) => {
    const cross = anchorPosition(layout.crossAnchor, vertical ? widthMm : heightMm, vertical ? o.widthMm : o.heightMm);
    const result = { widthMm: o.widthMm, heightMm: o.heightMm,
      xMm: vertical ? cross : start,
      yMm: heightMm - o.heightMm - (vertical ? start : cross) };
    start += sizes[i] + gap;
    return result;
  });
  return { heightMm, openings: resolved, gapMm: openings.length > 1 ? gap : null };
}
